import { yupResolver } from "@hookform/resolvers/yup"
import {
  Accordion,
  Button,
  Group,
  LoadingOverlay,
  Select,
  Tabs,
  Textarea,
  TextInput,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconArchive,
  IconArrowBackUp,
  IconBuildingCommunity,
  IconDeviceFloppy,
  IconTrash,
  IconUser,
} from "@tabler/icons-react"
import { debounce } from "lodash-es"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import * as yup from "yup"
import { CollectionQuery } from "../../../models/collection.model"
import { Owner } from "../../../models/owner.model"
import { Property } from "../../../models/property.model"
import ActivityLogWrapperComponent from "../../../shared/component/ActivityLog/activityLog-wrapper-component"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { Constants } from "../../../shared/constants/Constants"
import { useLazyGetActivitiesQuery } from "../../../shared/store/query/sharedQuery"
import {
  getCitiesByCountry,
  getUniqueCountryOptions,
  worldCities,
} from "../../../shared/utility/world-cities"
import {
  useArchivePropertyMutation,
  useCreatePropertyMutation,
  useDeletePropertyMutation,
  useLazyGetArchivedPropertyQuery,
  useLazyGetOwnersOfPropertyQuery,
  useLazyGetPropertyQuery,
  useRestorePropertyMutation,
  useTogglePropertyStatusMutation,
  useUpdatePropertyMutation,
} from "../store/property.query"
import { NewUnitComponent } from "./new-unit-component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"

interface Props {
  editMode: string
}

export default function NewPropertyComponent(props: Props) {
  const { editMode } = props
  const params = useParams()
  const { t } = useTranslation()
  const currentUser: any = JSON.parse(
    localStorage.getItem("userInfo") as string,
  )

  const [userInfo] = useCurrentUser()

  const [ownerSearch, setOwnerSearch] = useState<string | null>(null)
  const [ownersCollection, setOwnersCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })

  let schema: any = yup.object().shape({
    name: yup
      .string()
      .min(2, t("nameMin", { count: 2 }))
      .max(100, t("nameMax", { count: 100 }))
      .required(t("nameRequired")),

    type: yup
      .string()
      .typeError(t("typeValidation", { field: t("type"), type: t("string") }))
      .required(t("requiredValidation", { field: t("type") })),
    description: yup
      .string()
      .nullable()
      .typeError("Description should be a text."),
    // .required("Description is required."),
    // totalUnits: yup.number().typeError("Total Units should be a number").required("Total units is required").min(1, "Total units should be greater than 0."),
    ownerId: yup.string().when([], {
      is: currentUser?.role?.key == "owner",
      then: (schema: any) => schema.nullable(),
      otherwise: (schema: any) =>
        schema
          .typeError(
            t("typeValidation", {
              field: t("owner", { count: 1 }),
              type: t("string"),
            }),
          )
          .required(
            t("requiredValidation", { field: t("owner", { count: 1 }) }),
          ),
    }),
    address: yup
      .object()
      .shape({
        country: yup
          .string()
          .required(t("requiredValidation", { field: t("country") })),
        city: yup
          .string()
          .required(t("requiredValidation", { field: t("city") })),
        subCity: yup.string(),
        woreda: yup.string(),
        houseNumber: yup.string().nullable(),
        // .required("House Number is required"),
      })
      .required("Address is required"),
  })

  const defaultValue: Property = {
    name: "",
    type: "",
    address: {
      country: "",
      city: "",
      subCity: "",
      woreda: "",
      houseNumber: "",
    },
    ownerId: "",
    description: "",
    totalUnits: 0,
  }

  const [getProperty, property] = useLazyGetPropertyQuery()
  const [createProperty, newProperty] = useCreatePropertyMutation()
  const [updateProperty, updateResponse] = useUpdatePropertyMutation()
  const [restoreProperty, restoredProperty] = useRestorePropertyMutation()
  const [deleteProperty, deleteResponse] = useDeletePropertyMutation()
  const [getActivities, activities] = useLazyGetActivitiesQuery()
  const [archiveProperty, archiveResponse] = useArchivePropertyMutation()
  const [toggleStatus, toggleStatusResponse] = useTogglePropertyStatusMutation()
  const [getArchivedProperty, archivedProperty] =
    useLazyGetArchivedPropertyQuery()
  const [getOwners, owners] = useLazyGetOwnersOfPropertyQuery()

  const [opened, { open, close }] = useDisclosure()

  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Property>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  let selectedCountry = watch("address.country")
  const navigate = useNavigate()

  useEffect(() => {
    if (editMode === "detail") {
      if (params?.type && params?.type === "archived") {
        getArchivedProperty(`${params?.id}`).then((response: any) => {
          reset(response.data)
        })
      } else {
        getProperty({
          id: `${params?.id}`,
          query: { includes: ["owner"] },
        }).then((response: any) => {
          reset(response.data)
        })
      }
    } else {
      reset(defaultValue)
    }
  }, [params, editMode, getProperty])

  useEffect(() => {
    if (userInfo?.key?.role == "super_admin") {
      getOwners({
        ...ownersCollection,
        filter: [[{ field: "name", operator: "like", value: ownerSearch }]],
      })
    }
  }, [ownerSearch])

  useEffect(() => {
    if (userInfo?.key?.role == "super_admin") {
      getOwners(ownersCollection)
    }
  }, [ownersCollection, getOwners])

  function onSubmit(data: any) {
    if (currentUser?.role?.key != "super_admin") {
      data = { ...data, ownerId: currentUser?.id }
    }
    try {
      if (editMode === "new") {
        createProperty(data).then((response: any) => {
          if (!response?.error) {
            reset(defaultValue)
          }
        })
      } else {
        const property: Property = {
          id: params.id,
          ...data,
        }
        updateProperty(property)
      }
    } catch (e: any) {
      console.log("🚀 ~ onSubmit ~ e:", e)
    }
  }
  const onError = (error: any) => {
    console.log("🚀 ~ onError ~ error:", error)
  }

  function _parsePropertyOwners() {
    let data: { label: string; value: string }[] = []

    owners?.data?.data?.map((owner: Owner) => {
      data.push({ label: owner?.name as string, value: owner?.id as string })
    })
    if (!owners?.data?.data?.some((o) => o.id == property?.data?.ownerId)) {
      if (property?.data?.owner) {
        data.push({
          label: property?.data?.owner?.name as string,
          value: property?.data?.ownerId as string,
        })
      }
    }

    return data
  }

  return (
    // <div className="h-full relative w-full dark:bg-gray-700 dark:text-white flex space-x-2 justify-center">
    <>
      <ActivityLogWrapperComponent
        title={
          editMode === "new" ? (
            t("newProperty")
          ) : (
            <div className="h-full space-x-2 flex items-center">
              <div>{property?.data?.name}</div>
            </div>
          )
        }
        item={property}
        path={`/property/detail/${params?.type}/${params.id}`}
        id={params.id ?? ""}
      >
        <LoadingOverlay
          visible={
            (editMode === "new"
              ? newProperty.isLoading
              : updateResponse.isLoading) || property.isLoading
          }
        />
        <>
          <Tabs defaultValue="Vehicle">
            <Tabs.List>
              <Tabs.Tab value="Vehicle" icon={<IconUser size={14} />}>
                {t("propertyInfo")}
              </Tabs.Tab>

              {editMode == "detail" && (
                <Tabs.Tab
                  value="Units"
                  icon={<IconBuildingCommunity size={14} />}
                >
                  {t("unit", { count: 2 })}
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="Vehicle" pt="xs">
              <div className="flex  justify-center h-full">
                <form
                  onSubmit={handleSubmit(onSubmit, onError)}
                  className="w-full gap-y-4 dark:text-white"
                >
                  <Accordion
                    defaultValue={["info", "address", "emergency"]}
                    multiple
                    variant="separated"
                    classNames={{
                      control:
                        "bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl border border-gray-700",
                    }}
                  >
                    <Accordion.Item value="info" className="bg-gray-800">
                      <Accordion.Control className="bg-gray-800">
                        {t("propertyInfo")}
                      </Accordion.Control>
                      <Accordion.Panel className="bg-gray-800">
                        <div className="flex-col space-y-4 bg-gray-800">
                          <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                            <TextInput
                              classNames={{ label: "dark:text-white" }}
                              className="w-full"
                              error={errors.name && `${errors?.name?.message}`}
                              withAsterisk
                              label={t("name")}
                              placeholder={t("name")}
                              {...register("name")}
                            />
                            <TextInput
                              classNames={{ label: "dark:text-white" }}
                              className="w-full"
                              error={errors.type && `${errors?.type?.message}`}
                              withAsterisk
                              label={t("type")}
                              placeholder={t("type")}
                              {...register("type")}
                            />
                          </div>
                          <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                            {currentUser?.role?.key &&
                              currentUser?.role?.key == "super_admin" && (
                                <Controller
                                  control={control}
                                  name="ownerId"
                                  render={({ field }) => (
                                    <Select
                                      classNames={{ label: "dark:text-white" }}
                                      className="w-full"
                                      onKeyDown={debounce(
                                        (event: any) =>
                                          setOwnerSearch(event?.target?.value),
                                        1000,
                                      )}
                                      searchable
                                      clearable
                                      error={
                                        errors.ownerId &&
                                        `${errors?.ownerId?.message}`
                                      }
                                      withAsterisk
                                      label={t("owner", { count: 1 })}
                                      placeholder={t("owner")}
                                      data={_parsePropertyOwners()}
                                      {...field}
                                    />
                                  )}
                                />
                              )}

                            <Textarea
                              classNames={{ label: "dark:text-white" }}
                              className={
                                currentUser?.role?.key &&
                                currentUser?.role?.key == "super_admin"
                                  ? "w-full"
                                  : "w-[48%]"
                              }
                              error={
                                errors.description &&
                                `${errors?.description?.message}`
                              }
                              // withAsterisk
                              label={t("description")}
                              placeholder={t("description")}
                              {...register("description")}
                            />
                          </div>
                          {/* <div className="flex w-[48%] space-x-4  justify-between">
                           

                          </div> */}
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="address">
                      <Accordion.Control>{t("address")}</Accordion.Control>
                      <Accordion.Panel className="bg-gray-800">
                        <div className="flex-col space-y-4">
                          <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                            <Controller
                              control={control}
                              name="address.country"
                              render={({ field }) => (
                                <Select
                                  className="w-full"
                                  withAsterisk
                                  searchable
                                  nothingFound={
                                    <div className="fle w-full justify-center items-center">
                                      <EmptyIcon />
                                    </div>
                                  }
                                  error={
                                    errors.address?.country &&
                                    `${errors?.address?.country?.message}`
                                  }
                                  radius={"xs"}
                                  classNames={{
                                    label: "dark:text-white",
                                  }}
                                  label={t("country")}
                                  data={getUniqueCountryOptions(worldCities)}
                                  maxDropdownHeight={400}
                                  {...field}
                                />
                              )}
                            />

                            <Controller
                              control={control}
                              name="address.city"
                              render={({ field }) => (
                                <Select
                                  withAsterisk
                                  className="w-full"
                                  searchable
                                  nothingFound={
                                    <div className="fle w-full justify-center items-center">
                                      <EmptyIcon />
                                    </div>
                                  }
                                  error={
                                    errors.address?.city &&
                                    `${errors?.address?.city?.message}`
                                  }
                                  radius={"xs"}
                                  classNames={{
                                    label: "dark:text-white",
                                  }}
                                  defaultValue="United States"
                                  label={t("city")}
                                  placeholder={t("city")}
                                  data={getCitiesByCountry(selectedCountry)}
                                  maxDropdownHeight={400}
                                  {...field}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            error={
                              errors.address?.subCity &&
                              `${errors?.address?.subCity.message}`
                            }
                            label={t("subCity")}
                            placeholder={t("subCity")}
                            {...register("address.subCity")}
                          />
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            error={
                              errors.address?.woreda &&
                              `${errors?.address?.woreda?.message}`
                            }
                            label={t("woreda")}
                            placeholder={t("woreda")}
                            {...register("address.woreda")}
                          />
                        </div>
                        <div className="w-full md:w-[49%] flex space-x-4">
                          <TextInput
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            // withAsterisk
                            error={
                              errors.address?.houseNumber &&
                              `${errors?.address?.houseNumber?.message}`
                            }
                            label={t("houseNumber")}
                            placeholder={t("houseNumber")}
                            {...register("address.houseNumber")}
                          />
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>

                  <div className="w-full flex justify-end items-center">
                    <Group position="right" mt="xl">
                      {editMode === "new" && (
                        <>
                          <Button
                            classNames={{ label: "flex space-x-1 " }}
                            variant="default"
                            className="dark:text-white hover:bg-primary hover:dark:bg_dark_primary hover:text-white"
                            type="reset"
                            size="xs"
                          >
                            <span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="fill-current h-6"
                                viewBox="0 0 32 32"
                              >
                                <path d="M18.90625 4.09375C18.101563 4.09375 17.265625 4.367188 16.625 4.9375L16.625 4.96875L16.59375 5L4.90625 16.59375C3.695313 17.804688 3.703125 19.777344 4.84375 21.0625L4.875 21.09375L4.90625 21.09375L10.90625 27.09375C11.511719 27.699219 12.320313 27.992188 13.125 28L28 28L28 26L16.5 26L27 15.5C28.265625 14.234375 28.304688 12.210938 27.09375 11L21.09375 5C20.488281 4.394531 19.710938 4.09375 18.90625 4.09375 Z M 18.875 6.125C19.195313 6.125 19.492188 6.210938 19.6875 6.40625L25.6875 12.40625C26.074219 12.792969 26.128906 13.558594 25.59375 14.09375L20.5625 19.125L12.90625 11.46875L17.96875 6.4375L18 6.40625C18.253906 6.195313 18.570313 6.125 18.875 6.125 Z M 11.46875 12.90625L19.125 20.5625L14.03125 25.65625C14.019531 25.664063 14.011719 25.679688 14 25.6875C13.484375 26.117188 12.691406 26.066406 12.3125 25.6875L6.34375 19.75C6.328125 19.730469 6.328125 19.707031 6.3125 19.6875C5.902344 19.171875 5.9375 18.375 6.3125 18Z" />
                              </svg>
                            </span>
                            <span>{t("reset")}</span>
                          </Button>
                          <Button
                            variant="filled"
                            className="bg-primary text-white"
                            type="submit"
                            classNames={{ label: "flex space-x-1" }}
                            size={"xs"}
                            loading={newProperty.isLoading}
                          >
                            <span>
                              <IconDeviceFloppy size={22} strokeWidth={1.5} />
                            </span>
                            <span>{t("submit")}</span>
                          </Button>
                        </>
                      )}
                      {editMode === "detail" && (
                        <>
                          {(params?.type === "archived" &&
                            archivedProperty?.data?.deletedAt) ||
                          (params?.type === "active" &&
                            property?.data?.deletedAt) ? (
                            <>
                              <Confirmation
                                type={"danger"}
                                message={t("permanentDeleteMessage")}
                                onYes={() => deleteProperty(`${params?.id}`)}
                                header={t("permanentDeleteTitle")}
                              >
                                <Button
                                  variant="filled"
                                  loading={deleteResponse.isLoading}
                                  className={`bg-danger text-white flex space-x-2 items-center`}
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <IconTrash size={20} strokeWidth={1.5} />
                                  <span>{t("delete")}</span>{" "}
                                </Button>
                              </Confirmation>
                              <Confirmation
                                type={"notify"}
                                message={t("restoreMessage")}
                                onYes={() => restoreProperty(`${params?.id}`)}
                                header={t("restoreTitle")}
                              >
                                <Button
                                  variant="filled"
                                  loading={restoredProperty.isLoading}
                                  className={`bg-green-500 text-white flex space-x-2 items-center`}
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <IconArrowBackUp
                                    size={20}
                                    strokeWidth={1.5}
                                  />
                                  <span>{t("restore")}</span>
                                </Button>
                              </Confirmation>
                            </>
                          ) : (
                            <>
                              {" "}
                              <Confirmation
                                type={"notify"}
                                message={t("archiveMessage", {
                                  model: t("property", { count: 1 }),
                                })}
                                header={t("archiveTitle", {
                                  name: property?.data?.name,
                                })}
                                result={"single"}
                                resultRequired={true}
                                selectorLabel={t("selectAReason")}
                                labelDescription={t("archiveWhy", {
                                  model: t("property", { count: 1 }),
                                })}
                                options={Constants(t)?.ArchiveReason?.map(
                                  (reason) => {
                                    return { label: reason, value: reason }
                                  },
                                )}
                                customInput={true}
                                yesText={t("archive")}
                                onYes={(reason: string) => {
                                  archiveProperty({
                                    id: `${params.id}`,
                                    reason: reason,
                                  })
                                }}
                              >
                                <Button
                                  variant="filled"
                                  loading={archiveResponse.isLoading}
                                  className="bg-danger text-white"
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <span>
                                    <IconArchive size={22} strokeWidth={1.5} />
                                  </span>
                                  <span>{t("archive")}</span>
                                </Button>
                              </Confirmation>
                              <Confirmation
                                type={"notify"}
                                message={
                                  <span>
                                    {t("activateMessage", {
                                      action: property?.data?.enabled
                                        ? t("deactivate")
                                        : t("activate"),
                                    })}
                                  </span>
                                }
                                onYes={() => toggleStatus(`${params.id}`)}
                                header={t("activateTitle", {
                                  action: property?.data?.enabled
                                    ? t("deactivate")
                                    : t("activate"),
                                })}
                              >
                                <Button
                                  variant="filled"
                                  loading={toggleStatusResponse.isLoading}
                                  className={`${
                                    property.data?.enabled
                                      ? " bg-danger"
                                      : "bg-green-500"
                                  } text-white`}
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <span>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="fill-current h-6"
                                      viewBox="0 0 48 48"
                                    >
                                      <path d="M23.5 5C17.16639 5 12 10.16639 12 16.5C12 18.130861 12.341389 19.689962 12.957031 21.099609 A 1.50015 1.50015 0 1 0 15.707031 19.900391C15.252673 18.860038 15 17.713139 15 16.5C15 11.78761 18.78761 8 23.5 8C28.21239 8 32 11.78761 32 16.5C32 17.08427 31.94138 17.652314 31.830078 18.201172 A 1.50015 1.50015 0 1 0 34.769531 18.798828C34.92023 18.055686 35 17.28573 35 16.5C35 10.16639 29.83361 5 23.5 5 z M 23.5 12C21.032499 12 19 14.032499 19 16.5L19 25.759766L18.138672 25.404297C14.483804 23.900444 10.334734 26.436466 10.005859 30.375 A 1.50015 1.50015 0 0 0 10.693359 31.765625L19.957031 37.667969C20.601036 38.078718 21.151526 38.620029 21.576172 39.255859L23.033203 41.441406 A 1.50015 1.50015 0 0 0 23.035156 41.441406C23.803886 42.591886 25.189849 43.186167 26.554688 42.945312L31.882812 42.005859C33.603893 41.703285 34.998876 40.422039 35.449219 38.734375 A 1.50015 1.50015 0 0 0 35.451172 38.726562L37.832031 29.576172C38.653863 26.49462 36.64066 23.318721 33.501953 22.748047L28 21.748047L28 16.5C28 14.032499 25.967501 12 23.5 12 z M 23.5 15C24.346499 15 25 15.653501 25 16.5L25 23 A 1.50015 1.50015 0 0 0 26.232422 24.476562L32.964844 25.699219C34.424137 25.964545 35.315668 27.370273 34.933594 28.802734 A 1.50015 1.50015 0 0 0 34.929688 28.8125L32.550781 37.960938C32.399124 38.529274 31.940201 38.949356 31.363281 39.050781 A 1.50015 1.50015 0 0 0 31.363281 39.052734L26.033203 39.992188C25.834042 40.027338 25.642567 39.944908 25.529297 39.775391L24.072266 37.591797C23.417016 36.610136 22.565912 35.77367 21.570312 35.138672 A 1.50015 1.50015 0 0 0 21.568359 35.138672L13.251953 29.835938C13.814585 28.352335 15.413607 27.528548 16.996094 28.179688L19.929688 29.386719 A 1.50015 1.50015 0 0 0 22 28L22 16.5C22 15.653501 22.653501 15 23.5 15 z" />
                                    </svg>
                                  </span>
                                  {property?.data?.enabled ? (
                                    <span>{t("deactivate")}</span>
                                  ) : (
                                    <span>{t("activate")}</span>
                                  )}
                                </Button>
                              </Confirmation>
                              <Button
                                variant="filled"
                                className="bg-primary text-white"
                                type="submit"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                                loading={updateResponse.isLoading}
                              >
                                <span>
                                  <IconDeviceFloppy
                                    size={22}
                                    strokeWidth={1.5}
                                  />
                                </span>
                                <span>{t("update")}</span>
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </Group>
                  </div>
                </form>
              </div>
            </Tabs.Panel>

            {editMode == "detail" && (
              <Tabs.Panel value="Units" pt="xs">
                <NewUnitComponent
                  property={property}
                  open={open}
                  opened={opened}
                  close={close}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        </>
      </ActivityLogWrapperComponent>
    </>

    // </div >
  )
}
